import { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "antd";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { fetchUtility, getPlansForCro } from "../api/api";
import { PlanType, UtilityType } from "../types/planType";
import { getZIPCode } from "../api/api";
import PlanCard from "../cro/PlanCard";
import PlansPageSkeleton from "../cro/PlansPageSkeleton";
import PlansPageFilters from "../cro/PageFilters";
import ZipCode from "./modals/ZipCode";
import { staticCodes } from "../cro/common";
import { useModal } from "../cro/context/CroModalContext";
import InvalidZipcode from "./modals/InvalidZipcode";
import { btnOutline, btnUnderLine } from "../cro/styles/buttonStyles";
import { Add, Remove } from "@mui/icons-material";
import { injectIsFeaturedPlans } from "../utils/injectIsFeaturedPlans";
import { injectCustomDescription } from "../utils/injectCustomDescription";
import { injectSortOrder } from "../utils/injectSortOrder";
import { useTranslation } from "react-i18next";

const CroLandingPage = () => {
  const { showModal } = useModal();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("cro");
  const utilityId =
    searchParams.get("utility_id") ||
    searchParams.get("utilityId") ||
    searchParams.get("utilityid");

  const promocode =
    searchParams.get("promocode") ||
    searchParams.get("promoCode") ||
    searchParams.get("promo_code");

  const showMoreBtnRef = useRef<HTMLButtonElement>(null);
  const [plans, setPlans] = useState<PlanType[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [zipCodeLoading, setZipCodeLoading] = useState(false);
  const [utility, setUtility] = useState<UtilityType>({} as UtilityType);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [userZipCode, setUserZipCode] = useState<string | null>(null);
  const [isZipCodeValidForUtility, setIsZipCodeValidForUtility] =
    useState(false);
  const [stateCode, setStateCode] = useState<string | undefined>(undefined);

  const isTx = (utility as UtilityType)?.markets?.state_code === "TX";
  const utilityName = (utility as UtilityType)?.name || "";

  const selectedLanguage = localStorage.getItem("lang") || "en";

  useEffect(() => {
    const currentStateCode = (utility as UtilityType)?.markets?.state_code;
    if (currentStateCode) {
      setStateCode(currentStateCode);
    }
  }, [utility]);

  useEffect(() => {
    (async () => {
      if (userZipCode && utilityId) {
        setZipCodeLoading(true);
        await fetchUtility(userZipCode).then(
          (res: { status: number; response: { utility: any } }) => {
            if (res && res.status === 1) {
              const { utility } = res.response;
              const hasUtility = utility.some(
                (u: { id_utility: number }) =>
                  u.id_utility === Number(utilityId)
              );
              setIsZipCodeValidForUtility(hasUtility);
            } else {
              setIsZipCodeValidForUtility(false);
              console.error("Error fetching utility data:", res);
              return false;
            }
          }
        );
        setZipCodeLoading(false);
      }
    })();
  }, [userZipCode, utilityId]);

  useEffect(() => {
    (async function fetchUserZipCode() {
      const userZipCode = await getZIPCode();
      if (userZipCode) {
        setUserZipCode(userZipCode);
      }
    })();
  }, []);

  useEffect(() => {
    if (!utilityId) return;
    (async () => {
      setPageLoading(true);
      const response = await getPlansForCro({
        utility_id: utilityId,
        promocode,
        language: selectedLanguage,
      });
      if (response.status === 200) {
        const sortedPlans = response.data?.plans.sort(
          (a: PlanType, b: PlanType) => {
            return (Number(a?.Ef_Order) || 99) - (Number(b?.Ef_Order) || 99);
          }
        );
        const injectIsFeatured = await injectIsFeaturedPlans(
          sortedPlans as PlanType[],
          utilityId
        );
        const injectCustomDescriptions = await injectCustomDescription(
          (injectIsFeatured || []) as PlanType[],
          stateCode
        );
        const injectCustomSortOrder = await injectSortOrder(
          injectCustomDescriptions as PlanType[],
          stateCode,
          utilityId
        );

        setPlans(injectCustomSortOrder as PlanType[]);
        setPageLoading(false);
        setUtility(response.data.utilities[utilityId] || {});
      } else {
        console.error("Error fetching plans:", response.statusText);
        setPageLoading(false);
      }
    })();
  }, [utilityId, promocode, stateCode, selectedLanguage]);

  if (!utilityId) {
    return (
      <main className="container my-4" style={{ minHeight: "50vh" }}>
        <Helmet>
          <title>Invalid URL | CleanSky Energy</title>
          <meta name="description" content="Invalid URL. Please provide a valid utility to view energy plans." />
        </Helmet>
        <Row gutter={[16, 16]} justify="center" align="middle">
          <Col span={24} style={{ textAlign: "center" }}>
            <h1 className="h4 fw-bold m-0 p-0 display-4 my-4 roboto-semibold">
              {t("Invalid URL. No Utility Provided")}
            </h1>
            <p>{t("Please enter a valid utility to view plans")}</p>
          </Col>
        </Row>
      </main>
    );
  }

  if (pageLoading)
    return (
      <main
        className="container my-4 roboto-semibold"
        style={{ minHeight: "50vh" }}
      >
        <Helmet>
          <title>Loading Plans... | CleanSky Energy</title>
          <meta name="description" content="Loading energy plans. Please wait..." />
        </Helmet>
        <Row gutter={[16, 16]} justify="center" align="middle">
          <PlansPageSkeleton />
        </Row>
      </main>
    );

  if (!plans.length) {
    return (
      <main
        className="container my-4 roboto-semibold"
        style={{ minHeight: "50vh" }}
      >
        <Helmet>
          <title>{`No Plans Available for ${utilityName || `Utility ${utilityId}`} | CleanSky Energy`}</title>
          <meta name="description" content={`Sorry, CleanSky Energy currently does not service the area for ${utilityName || `utility ${utilityId}`}. Please check back later.`} />
        </Helmet>
        <Row gutter={[16, 16]} justify="center" align="middle">
          <Col span={20} style={{ textAlign: "center" }}>
            <h1 className="h4 fw-bold m-0 p-0 display-5 my-4">
              {t(
                "We're sorry, CleanSky Energy currently does not service this area"
              )}
            </h1>
            <p>{t("Please check back later")}</p>
          </Col>
        </Row>
      </main>
    );
  }

  const displayedPlans = plans.slice(0, showAllPlans ? plans.length : 3);

  // Generate dynamic title based on utility
  const getPageTitle = () => {
    if (utilityName) {
      return `Plans for ${utilityName} | CleanSky Energy`;
    }
    if (utilityId) {
      return `Plans for Utility ${utilityId} | CleanSky Energy`;
    }
    return "Energy Plans | CleanSky Energy";
  };

  return (
    <main className="container">
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={`Find the best energy plans for ${utilityName || `utility ${utilityId}`}. Compare electricity and natural gas plans from CleanSky Energy.`} />
      </Helmet>
      <Row
        gutter={[16, 16]}
        justify="start"
        style={{
          margin: "1.5rem 0",
        }}
      >
        <Col span={24} style={{ textAlign: "center", margin: "20 0" }}>
          <Row justify={"center"} align={"middle"}>
            <Col>
              <span>
                {t("Service zip code:")}
                {userZipCode && (
                  <span className="roboto-semibold">{userZipCode}</span>
                )}
                <button
                  onClick={() => {
                    showModal(
                      { title: t("Edit zip code") },
                      {
                        content: (
                          <ZipCode
                            userZipCode={userZipCode}
                            setUserZipCode={setUserZipCode}
                          />
                        ),
                      }
                    );
                  }}
                  style={btnUnderLine}
                >
                  {t("Edit")}
                </button>
              </span>
            </Col>
            <Col className="d-none d-md-flex">
              <span style={{ padding: "0 .25rem" }}></span> |{" "}
              <span style={{ padding: "0 .25rem" }}></span>
            </Col>
            <Col>
              {t("Utility provider:")}
              <span className="roboto-semibold">
                {utility.name || utility.friendly_name}
              </span>
            </Col>
          </Row>
        </Col>
        <Col span={24} className="text-center">
          {plans && plans[0]?.commodity_name === "Electric" ? (
            <h1 className="fw-bold m-0 p-0 lg:display-4 my-2 roboto-bold">
              {t("Select The Best Electricity Plan For You")}
            </h1>
          ) : null}
          {plans && plans[0]?.commodity_name === "Gas" ? (
            <h1 className="fw-bold m-0 p-0 lg:display-4 my-2 roboto-bold">
              {t("Select The Best Natural Gas Plan For You")}
            </h1>
          ) : null}
        </Col>
        <Col span={24} style={{ textAlign: "center", zIndex: 99 }}>
          <PlansPageFilters
            plans={plans}
            setPlans={setPlans}
            commodityName={plans.length ? plans[0]?.commodity_name : ""}
          />
        </Col>
        <Col span={24}>
          <Row gutter={[40, 40]} justify="start" align="bottom">
            {displayedPlans.map((plan) => (
              <Col
                key={plan.plan_id}
                xs={24}
                sm={24}
                md={12}
                lg={8}
                style={{ marginBottom: 20 }}
              >
                <PlanCard
                  // plan.ef_plan_details?.calloutText
                  // if the above object exists, then it is featured
                  isFeatured={plan?.isFeatured}
                  utilityName={utilityName}
                  utilityId={utilityId}
                  userZipCode={
                    (() => {
                      const staticZipCode = staticCodes.find(
                        (code) =>
                          utilityId && Object.keys(code).includes(utilityId)
                      );
                      return isZipCodeValidForUtility
                        ? userZipCode
                        : staticZipCode && Object.values(staticZipCode)[0];
                    })() || ""
                  }
                  // isFeatured={plan.plan_name === "Embrace Green 3"}
                  zipCodeLoading={zipCodeLoading}
                  plan={plan}
                  isTypeVariable={
                    stateCode === "PA" &&
                    plan.plan_name.includes("Embrace Green 3")
                  }
                  isTx={isTx}
                  isPa={stateCode === "PA"}
                  selectPlan={() => {
                    // optional prop, to either redirect to signup or go to next step
                    if (userZipCode && !isZipCodeValidForUtility) {
                      showModal(
                        {
                          title: t(
                            "We're sorry, the zip code {{userZipCode}} is not valid for {{utilityName}}",
                            {
                              userZipCode,
                              utilityName: utility.formal_name,
                            }
                          ),
                        },
                        {
                          content: (
                            <InvalidZipcode setUserZipCode={setUserZipCode} />
                          ),
                        },
                        {
                          size: "lg",
                        }
                      );
                      return;
                    }

                    const params = new URLSearchParams(window.location.search);
                    const currentParams = params
                      .toString()
                      .replace(/utilityId=[^&]*/, ""); // remove utilityId from params

                    const staticZipCode = staticCodes.find(
                      (code) =>
                        utilityId && Object.keys(code).includes(utilityId)
                    );

                    const initialzipCode = isZipCodeValidForUtility
                      ? userZipCode
                      : staticZipCode && Object.values(staticZipCode)[0];

                    const url = `/?zipcode=${initialzipCode}&utilityId=${utilityId}&planId=${plan.plan_id}&commodity=${plan.commodity_name}&${currentParams}&linkSource=cro`;

                    window.open(url, "_self");
                  }}
                />
              </Col>
            ))}
          </Row>
        </Col>
        {plans.length > 3 && (
          <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
            <Button
              style={btnOutline}
              className="roboto-semibold"
              onClick={() => {
                if (showAllPlans) {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else if (showMoreBtnRef.current) {
                  showMoreBtnRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }
                setShowAllPlans((prev) => !prev);
              }}
            >
              {showAllPlans ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {t("SHOW LESS PLANS")}
                  <Remove
                    style={{
                      margin: "0 0.25rem",
                      fontSize: "1rem",
                    }}
                  />
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {t("SEE MORE PLANS")}
                  <Add
                    style={{
                      margin: "0 0.25rem",
                      fontSize: "1rem",
                    }}
                  />
                </div>
              )}
            </Button>
          </Col>
        )}
      </Row>
    </main>
  );
};

export default CroLandingPage;
